import { useState, useEffect, JSX } from "react";

import { Grid, Box, LinearProgress, Alert } from "@mui/material";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchEmbeddingConfig, fetchGuestToken } from "src/api/extendedAnalytics.api";
import { SUPERSET_DOMAIN } from "src/lib/config.helper";
import { type SupersetEmbeddedDashboardConfiguration } from "src/types/superset";

const styles = {
  fullWidth: { xs: 12 },
  superSetContainer: { width: "100%", height: 800, border: "none" },
};

function ExtendedAnalyticsViewPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const superSetDashboardId = Number.parseInt(id ?? "", 10);
  const [guestTokenGenerationError, setGuestTokenGenerationError] = useState<boolean>(false);
  const [rendered, setRendered] = useState<boolean>(false);

  const {
    data: embedingConfig,
    isLoading,
    error,
  } = useQuery<SupersetEmbeddedDashboardConfiguration>({
    queryKey: ["embeddingUUID", { superSetDashboardId }],
    queryFn: async () => fetchEmbeddingConfig(superSetDashboardId),
    enabled: true,
    retry: 1,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const embedDashboardAsync = async (): Promise<void> => {
      if (embedingConfig && !rendered) {
        try {
          await embedDashboard({
            id: embedingConfig.embeddingUUID, // from the Embedded dialog
            supersetDomain: SUPERSET_DOMAIN, // from the Embedded dialog

            mountPoint: document.querySelector("#my-superset-container")!, // any HTML element that can contain an iframe
            fetchGuestToken: async () => {
              try {
                const guestTokenDTO = await fetchGuestToken(superSetDashboardId);
                return guestTokenDTO.guestToken;
              } catch (error: unknown) {
                setGuestTokenGenerationError(true);
                throw error;
              }
            }, // function responsible to return a guest_token
            dashboardUiConfig: {}, // dashboard UI configuration. Options: hideTitle, hideChartControls, filters.expanded, urlParams (all optional)
          });

          const iframe = document.querySelector("iframe");
          if (iframe) {
            iframe.style.width = "100%"; // Set the width of the iframe
            iframe.style.minHeight = "100vh"; // Set the height of the iframe
            iframe.style.border = "none"; // Set the height of the iframe
          }

          setRendered(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error embedding dashboard:", error);
        }
      }
    };

    void embedDashboardAsync();
  }, [embedingConfig, rendered, superSetDashboardId]);

  return (
    <Grid container>
      <Grid size={styles.fullWidth}>
        {isLoading && !rendered && <LinearProgress />}
        {(!!error || guestTokenGenerationError) && (
          <Alert severity='error'>{t("There was an error while fetching extended analytics dashboards")}</Alert>
        )}
        <Box id='my-superset-container' sx={styles.superSetContainer} />
      </Grid>
    </Grid>
  );
}

export default ExtendedAnalyticsViewPage;
